// src/pages/home/discover/components/HomeArtlists.jsx
import { useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from "@/i18n/useTranslations";
import HomeCarouselAnalyticsService from "@/services/homeCarouselAnalytics.service";
import ArtlistSliderCard from "@/shared-components/cards/components/ArtlistSliderCard";
import ForwardArrowIcon from "@/shared-components/icons/components/public/ForwardArrowIcon";

import useCarouselImpressionTracking from "./hooks/useCarouselImpressionTracking";

import "./styles/HomeArtlists.css";

export default function HomeArtlists({
  artlists = [],    // array de objetos ArtlistSummary
  title = "",
  moreLink = "",
  trackingContext = {},
}) {
  const tHome = useTranslations('common.shared.home');
  const navigate = useNavigate();
  const { state } = useStateHandler();
  const impressionRef = useCarouselImpressionTracking(trackingContext);
  const [page, setPage] = useState(0);
  const handleSeeAll = () => {
    HomeCarouselAnalyticsService.trackHomeCarouselEvent(
      "carousel_see_all_click",
      {
        ...trackingContext,
        destination: moreLink || "/search",
      },
      { userSession: state.user_session }
    );

    if (moreLink) {
      navigate(moreLink);
      return;
    }
    navigate("/search", { state: { category: "Artlists" } });
  };

  const isSingleColumn = useMediaQuery({ query: "(max-width: 650px)" });
  const perPage = isSingleColumn ? 1 : 2;

  const maxPage = useMemo(
    () => Math.max(0, Math.ceil(artlists.length / perPage) - 1),
    [artlists.length, perPage]
  );

  useEffect(() => {
    setPage((current) => Math.min(current, maxPage));
  }, [maxPage]);

  const visible = useMemo(
    () => artlists.slice(page * perPage, page * perPage + perPage),
    [artlists, page, perPage]
  );

  const shouldShowPager = isSingleColumn ? artlists.length > 1 : artlists.length > 2;

  const prev = () => setPage((p) => Math.max(0, p - 1));
  const next = () => setPage((p) => Math.min(maxPage, p + 1));

  return (
    <div ref={impressionRef} className="home-artlists__wrapper">
      <div className="home-slider__header">
        {title ? <h2 className="home-title">{title}</h2> : <span />}
        <div className="home-artlists__see-all">
          <button
            type="button"
            className="text-custom__component home-slider__see-all font-size-medium"
            onClick={handleSeeAll}
          >
            {tHome('see_all')}
          </button>
        </div>
      </div>

      <div className="home-artlists__slides">
        {visible.map((art) => (
          <div
            key={art._id || art.id}
            className="home-artlists__slide"
            onClick={() => {
              const artId = art._id || art.id;
              if (!artId) return;
              HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                "carousel_item_click",
                {
                  ...trackingContext,
                  itemId: artId,
                  itemKind: "artlist",
                  destination: `/artlist/${encodeURIComponent(artId)}`,
                },
                { userSession: state.user_session }
              );
              navigate(`/artlist/${encodeURIComponent(artId)}`);
            }}
          >
            <ArtlistSliderCard artlist={art} />
          </div>
        ))}
      </div>

      {shouldShowPager && (
        <div className="home-artlists__pager">
          <button onClick={prev} disabled={page === 0} className="home-artlists__btn">
            <ForwardArrowIcon className="home-artlists__btn-icon prev" />
          </button>
          <button onClick={next} disabled={page === maxPage} className="home-artlists__btn">
            <ForwardArrowIcon className="home-artlists__btn-icon next" />
          </button>
        </div>
      )}
    </div>
  );
}
